import { useEffect, useState } from "react";
import { Button, Card, Spinner, Text } from "@fluentui/react-components";
import {
  getMeetingReportDetail,
  type MeetingReport,
  type MeetingReportDetail,
} from "../services/apiService";
import { sanitizeHtml } from "../services/sanitizeHtml";
import { useLanguage } from "../i18n/LanguageContext";

function formatFullDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface ReportViewProps {
  jwt: string;
  report: MeetingReport;
  onBack: () => void;
}

/** Full AI meeting report (summary HTML) for a single meeting. */
export function ReportView({ jwt, report, onBack }: ReportViewProps) {
  const [detail, setDetail] = useState<MeetingReportDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { t } = useLanguage();

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    getMeetingReportDetail(jwt, report.eventId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : t("reportView.failedToLoad"));
      });
    return () => {
      cancelled = true;
    };
  }, [jwt, report.eventId, t]);

  return (
    <main style={{ maxWidth: 760, margin: "0 auto", padding: "1.25rem" }}>
      <Button appearance="subtle" onClick={onBack} style={{ marginBottom: "0.75rem" }}>
        {t("reportView.back")}
      </Button>

      <Card style={{ padding: "1.25rem", borderTop: "3px solid var(--colorBrandBackground)" }}>
        <Text size={500} weight="semibold" block>
          {report.name}
        </Text>
        <Text size={200} block style={{ color: "var(--colorNeutralForeground3)", marginTop: "0.25rem" }}>
          {formatFullDate(report.startDateTime)}
          {report.participantCount > 0 ? ` · ${report.participantCount} ${t("reports.participants")}` : ""}
        </Text>

        {!detail && !error && (
          <div style={{ padding: "2rem 0" }}>
            <Spinner label={t("reportView.loadingSummary")} />
          </div>
        )}

        {error && (
          <Text block style={{ marginTop: "1rem", color: "var(--colorPaletteRedForeground1)" }}>
            {error}
          </Text>
        )}

        {detail && !detail.hasSummary && (
          <Text block style={{ marginTop: "1rem", color: "var(--colorNeutralForeground2)" }}>
            {t("reportView.notReady", {
              status: detail.processStatus ? t("reportView.statusSuffix", { status: detail.processStatus }) : "",
            })}
          </Text>
        )}

        {detail && detail.hasSummary && (
          <div
            style={{ marginTop: "1rem", lineHeight: 1.55, overflowWrap: "anywhere" }}
            // Sanitized output of our own backend's AI summary HTML.
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(detail.summaryHtml) }}
          />
        )}
      </Card>
    </main>
  );
}
